import { useEffect, useRef, useState } from "react"
import { hardwareProducts } from "./hardwareShowcaseProducts"
import "./hardwareShowcase.css"

// The landing page needs no device/API session. Load the local 3D module and
// CAD asset only when this particular product approaches the viewport.
export default function HardwareShowcase({ product }) {
  const spec = hardwareProducts[product]
  const host = useRef(null)
  const surface = useRef(null)
  const engine = useRef(null)
  const openRef = useRef(false)
  const [visible, setVisible] = useState(false)
  const [opened, setOpened] = useState(false)
  const [status, setStatus] = useState("idle")
  const [attempt, setAttempt] = useState(0)
  openRef.current = opened

  useEffect(() => {
    if (!window.IntersectionObserver) return // Explicit button still works.
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true)
        observer.disconnect()
      }
    }, { rootMargin: "180px" })
    observer.observe(host.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!visible) return
    let cancelled = false
    let instance
    const abort = new AbortController()
    setStatus("loading")
    import("./HardwareShowcaseScene").then(({ createHardwareShowcase }) => {
      if (cancelled) return
      instance = createHardwareShowcase(surface.current, spec, {
        signal: abort.signal,
        onToggle: () => setOpened(value => !value),
        onError: () => { if (!cancelled) setStatus("error") },
      })
      engine.current = instance
      return instance.ready.then(() => {
        if (cancelled) return
        instance.setOpen(openRef.current)
        setStatus("ready")
      })
    }).catch(error => {
      if (!cancelled && error.name !== "AbortError") setStatus("error")
    })
    return () => {
      cancelled = true
      abort.abort()
      instance?.dispose()
      if (engine.current === instance) engine.current = null
    }
  }, [visible, attempt, spec])

  useEffect(() => { engine.current?.setOpen(opened) }, [opened])

  const toggle = () => {
    setVisible(true)
    if (status === "error") { setAttempt(value => value + 1); return }
    setOpened(value => !value)
  }
  const active = status === "ready"
  const state = active ? (opened ? "Gehäuse geöffnet" : "Gehäuse geschlossen")
    : status === "loading" ? "3D-Modell wird geladen …"
    : status === "error" ? "3D-Ansicht nicht verfügbar. Produktansicht bleibt sichtbar." : "Gehäuse & Elektronik"

  return <figure className={`fh-showcase fh-${product}`} ref={host} aria-label={`${spec.name} – Gehäuse und bestückte Platine`}>
    <div className="fh-heading"><span>{spec.label}</span><span className="fh-revision">{spec.revision}</span></div>
    <div className="fh-stage" id={`hardware-${product}`}>
      <img className={`fh-poster${active ? " fh-poster-hidden" : ""}`} src={spec.poster} alt={`${spec.name}: aktuelles CAD-Modell des Gehäuses mit eingebauter Elektronik`} loading="lazy" decoding="async" width="1200" height="760"/>
      <div className="fh-canvas" ref={surface} aria-hidden="true" />
      <div className="fh-tools" aria-label={`${spec.name} Ansicht`}>
        <button type="button" disabled={!active} onClick={() => engine.current?.zoom(1)} aria-label={`${spec.name} vergrößern`}>+</button>
        <button type="button" disabled={!active} onClick={() => engine.current?.zoom(-1)} aria-label={`${spec.name} verkleinern`}>−</button>
        <button type="button" disabled={!active} onClick={() => engine.current?.reset()} aria-label={`${spec.name} Ansicht zurücksetzen`}>↺</button>
      </div>
    </div>
    <figcaption className="fh-caption">
      <div><p className="fh-state" role="status"><span aria-hidden="true"/>{state}</p><p className="fh-hint">{active ? `Ziehen: drehen · Mausrad / zwei Finger: zoomen · Klicken: ${opened ? "schließen" : "öffnen"}` : "Ein Blick unter die Oberfläche."}</p></div>
      <button className="fh-open" type="button" aria-expanded={opened && active} aria-controls={`hardware-${product}`} onClick={toggle}>
        {status === "error" ? "3D erneut laden" : opened ? "Gehäuse schließen" : "Innenleben ansehen"}<span aria-hidden="true">{opened ? "−" : "↗"}</span>
      </button>
    </figcaption>
    <p className="fh-note">Aktueller CAD-Stand · Gehäuse öffnet sich, Elektronik bleibt montiert.{spec.displaySurfaceName && " Display: Original-Screenshot, keine Livewerte."}</p>
  </figure>
}
